/**
 * Regional date display for the member portal.
 *
 * English DMY numeric — day / month / year — so every screen shows the same
 * shape regardless of the browser locale (which would otherwise flip to MDY
 * on en-US machines):
 *
 *   07/08/2026
 *   25/08/2026
 */

const DATE_LOCALE = 'en-GB';

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  ...DATE_OPTS,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

/** Calendar date only: `07/08/2026`. */
export function formatDate(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  return d.toLocaleDateString(DATE_LOCALE, DATE_OPTS);
}

/** Date + time, DMY numeric date portion: `07/08/2026, 14:30:00`. */
export function formatDateTime(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  return d.toLocaleString(DATE_LOCALE, DATETIME_OPTS);
}

/** Date + time without seconds — security-history rows match the mock (`07/08/2026, 14:30`). */
export function formatDateTimeShort(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  return d.toLocaleString(DATE_LOCALE, {
    ...DATE_OPTS,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Formats an API calendar day (`YYYY-MM-DD`) without a timezone shift.
 * `new Date('2026-08-07')` is UTC midnight and can render as the previous
 * local day — so we rearrange the digits instead.
 */
export function formatIsoDate(isoDay: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay.trim());
  if (!m) return formatDate(isoDay);
  return `${m[3]}/${m[2]}/${m[1]}`;
}
